import { type FC, useState } from "react";

import type { WorkspaceController } from "./useWorkspaceController";

import { DEFAULT_CHANNEL_ENVIRONMENT_FORM } from "../useChannelEnvironment";
import { RefreshRepositoryRejectedUiError } from "../useChannelWorkspaceRepositories";
import { AttachRepoModal } from "./AttachRepoModal";
import { ChangeBranchModal } from "./ChangeBranchModal";
import { ConfirmModal } from "./ConfirmModal";
import { EnvironmentFormModal } from "./EnvironmentFormModal";
import {
  canForceRefreshRepository,
  formValuesFromEnvironment,
  getPatchConfirmation,
  getRepoAcknowledgeAbsentDetachMessage,
  getRepoDetachMessage,
} from "./environmentUtils";
import { ResumeRefusalModal } from "./ResumeRefusalModal";

interface WorkspaceDialogsProps {
  controller: WorkspaceController;
}

export const WorkspaceDialogs: FC<WorkspaceDialogsProps> = ({ controller }) => {
  const [attachProgress, setAttachProgress] = useState<string | null>(null);
  const {
    acknowledgeAbsentRepositoryDetach,
    actionError,
    actionErrorCause,
    archive,
    attach,
    attachRepository,
    attachedRepoKeys,
    busy,
    changeRepositoryBranch,
    channel,
    clearResumeRefusal,
    closeDialog,
    detach,
    detachRepository,
    dialog,
    environment,
    openDialog,
    patch,
    profiles,
    profilesError,
    profilesLoading,
    refreshOverride,
    refreshRepository,
    restart,
    resumeRefusal,
    resumeRetry,
    runAction,
    setRefreshOverride,
    setRefreshResult,
  } = controller;

  return (
    <>
      {resumeRefusal !== null && (
        <ResumeRefusalModal
          channelId={channel.id}
          refusal={resumeRefusal}
          retrying={busy !== null}
          canEditResources={environment !== null}
          onRetry={() => {
            if (resumeRetry) {
              void runAction(resumeRetry.label, resumeRetry.run, {
                autoClose: false,
              });
            }
          }}
          onEditResources={() => {
            clearResumeRefusal();
            openDialog({ kind: "edit" });
          }}
          onClose={clearResumeRefusal}
          onRecoveryPrepared={async () => {
            clearResumeRefusal();
            await controller.refresh();
          }}
        />
      )}
      {dialog.kind === "attach" && (
        <EnvironmentFormModal
          title="Attach Environment"
          initialValues={DEFAULT_CHANNEL_ENVIRONMENT_FORM}
          submitLabel="Attach"
          busy={busy === "attach"}
          errorMessage={actionError}
          profiles={profiles}
          profilesError={profilesError}
          profilesLoading={profilesLoading}
          showProfileSelector
          onClose={closeDialog}
          onSubmit={(values) =>
            void runAction("attach", async () => await attach(values))
          }
        />
      )}
      {dialog.kind === "edit" && environment && (
        <EnvironmentFormModal
          title="Edit Environment"
          initialValues={formValuesFromEnvironment(environment)}
          submitLabel="Apply"
          busy={busy === "patch"}
          errorMessage={actionError}
          profiles={profiles}
          profilesLoading={profilesLoading}
          onClose={closeDialog}
          onSubmit={(values) =>
            void runAction(
              "patch",
              async () => {
                const response = await patch(values);
                if (response.requiresReplacement || response.requiresRestart) {
                  controller.openDialog({
                    kind: "patch-confirm",
                    message: getPatchConfirmation(response).message,
                    response,
                    title: getPatchConfirmation(response).title,
                    values,
                  });
                  return;
                }
                controller.openDialog({ kind: "none" });
              },
              { autoClose: false },
            )
          }
        />
      )}
      {dialog.kind === "patch-confirm" && (
        <ConfirmModal
          title={dialog.title}
          message={dialog.message}
          confirmLabel={getPatchConfirmation(dialog.response).confirmLabel}
          tone="danger"
          busy={busy === "patch-confirm"}
          errorMessage={actionError}
          onClose={closeDialog}
          onConfirm={() =>
            void runAction("patch-confirm", async () => {
              await patch(dialog.values, { confirmRestart: true });
              controller.openDialog({ kind: "none" });
            })
          }
        />
      )}
      {dialog.kind === "archive-confirm" && (
        <ConfirmModal
          title="Hibernate Environment"
          message="Hibernate this environment? Running spawns stop and all workspace, container, and temporary filesystem state is retained. Resuming requires capacity on the pinned node."
          confirmLabel="Hibernate"
          tone="neutral"
          busy={busy === "archive"}
          errorMessage={actionError}
          onClose={closeDialog}
          onConfirm={() => void runAction("archive", archive)}
        />
      )}
      {dialog.kind === "detach-confirm" && (
        <ConfirmModal
          title="Delete Environment"
          message="Hibernate and soft-delete this environment. Retained state and history remain for audit, but the environment cannot be resumed."
          confirmLabel="Delete"
          tone="danger"
          busy={busy === "detach"}
          errorMessage={actionError}
          onClose={closeDialog}
          onConfirm={() => void runAction("detach", detach)}
        />
      )}
      {dialog.kind === "attach-repo" && (
        <AttachRepoModal
          onAttach={attachRepository}
          attachedRepoKeys={attachedRepoKeys}
          busy={busy === "attach-repo"}
          errorCause={actionErrorCause}
          errorMessage={actionError}
          progressMessage={attachProgress}
          onClose={() => {
            setAttachProgress(null);
            closeDialog();
          }}
          onSubmit={(input) =>
            void runAction(
              "attach-repo",
              async () =>
                await attachRepository(input, {
                  onProgress: setAttachProgress,
                }),
            )
          }
        />
      )}
      {dialog.kind === "change-branch" &&
        (() => {
          // Always act on the CURRENT entity — the dialog's snapshot goes
          // stale if convergence completes while it is open, which would
          // mislabel "Cancel pending change" into a reversal request. The
          // key remounts the modal whenever applied/pending state moves so
          // its local selection re-derives.
          const currentRepository =
            controller.repositories.find(
              (repository) => repository.id === dialog.repository.id,
            ) ?? null;
          if (!currentRepository) {
            return null;
          }
          return (
            <ChangeBranchModal
              key={`${currentRepository.id}:${currentRepository.defaultBranch ?? ""}:${currentRepository.pendingBranch ?? ""}`}
              repository={currentRepository}
              busy={busy === "change-branch"}
              errorMessage={actionError}
              onClose={closeDialog}
              onSubmit={(branch) =>
                void runAction("change-branch", async () => {
                  await changeRepositoryBranch(currentRepository.id, branch);
                })
              }
            />
          );
        })()}
      {dialog.kind === "detach-repo" && (
        <ConfirmModal
          title="Detach Repository"
          message={getRepoDetachMessage(dialog.repository, environment)}
          confirmLabel="Detach"
          tone="danger"
          busy={busy === "detach-repo"}
          errorMessage={actionError}
          onClose={closeDialog}
          onConfirm={() =>
            void runAction("detach-repo", async () => {
              if (dialog.kind === "detach-repo") {
                await detachRepository(dialog.repository.id);
              }
            })
          }
        />
      )}
      {dialog.kind === "acknowledge-absent-detach" && (
        <ConfirmModal
          title="Acknowledge Absent Checkout"
          message={getRepoAcknowledgeAbsentDetachMessage(dialog.repository)}
          confirmLabel="Acknowledge"
          tone="danger"
          busy={busy === "acknowledge-absent-detach"}
          errorMessage={actionError}
          onClose={closeDialog}
          onConfirm={() =>
            void runAction("acknowledge-absent-detach", async () => {
              if (dialog.kind === "acknowledge-absent-detach") {
                await acknowledgeAbsentRepositoryDetach(dialog.repository.id);
              }
            })
          }
        />
      )}
      {dialog.kind === "refresh-repo" && (
        <ConfirmModal
          title="Refresh Repository"
          message={`Fast-forward ${dialog.repository.remoteSlug} to the latest ${dialog.repository.defaultBranch ?? dialog.repository.repositoryDefaultBranch ?? "default"} branch head? Running spawns must be stopped first.`}
          confirmLabel="Refresh"
          tone="neutral"
          busy={busy === "refresh-repo"}
          errorMessage={actionError}
          onClose={closeDialog}
          onConfirm={() =>
            void runAction("refresh-repo", async () => {
              if (dialog.kind === "refresh-repo") {
                try {
                  const response = await refreshRepository(
                    dialog.repository.id,
                  );
                  setRefreshResult(response.refreshResult ?? null);
                } catch (caughtError) {
                  if (caughtError instanceof RefreshRepositoryRejectedUiError) {
                    setRefreshOverride({
                      reason: caughtError.rejectReason,
                      repositoryId: dialog.repository.id,
                    });
                  }
                  throw caughtError;
                }
              }
            })
          }
          secondaryConfirmLabel={
            refreshOverride?.repositoryId === dialog.repository.id &&
            canForceRefreshRepository(refreshOverride.reason)
              ? "Force Refresh"
              : undefined
          }
          secondaryTone="danger"
          secondaryBusy={busy === "force-refresh-repo"}
          onSecondaryConfirm={() =>
            void runAction("force-refresh-repo", async () => {
              if (dialog.kind === "refresh-repo") {
                const response = await refreshRepository(dialog.repository.id, {
                  force: true,
                });
                setRefreshResult(response.refreshResult ?? null);
              }
            })
          }
        />
      )}
      {dialog.kind === "restart-confirm" && (
        <ConfirmModal
          title="Apply pending changes"
          message="Pending repository changes apply only on a workspace restart — there's no in-place apply. Restarting stops every running spawn in this channel and re-clones attached repos on startup."
          confirmLabel="Apply by restarting"
          tone="danger"
          busy={busy === "restart"}
          errorMessage={actionError}
          onClose={closeDialog}
          onConfirm={() => void runAction("restart", restart)}
        />
      )}
    </>
  );
};
